import { SectionTag } from '@/components/SectionTag';
import { PricingTierCard } from '@/components/PricingTierCard';
import { TestimonialCard } from '@/components/TestimonialCard';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Check, Minus } from 'lucide-react';

const TIERS = [
  {
    tier: 'Tier 1',
    name: 'THC Connect',
    price: '\u20B99,999 / year',
    tagline: 'For professionals and early founders exploring healthcare innovation.',
    features: [
      'THC Community (Slack / WhatsApp)',
      'Weekly AMAs with clinicians & founders',
      '50% discounts to open events',
      'Curated resources & workshops',
      'Warm intros & clinician matchmaking',
      'Community dashboard access',
    ],
    cta: 'Apply Now',
    testId: 'membership-tier-starter',
  },
  {
    tier: 'Tier 2',
    name: 'ASCENT',
    price: '\u20B949,999 / cohort',
    tagline: 'For founders building serious healthcare businesses.',
    features: [
      'Everything in THC Connect',
      'ASCENT program (clinical, GTM & funding)',
      'Curated intros to clinicians & operators',
      'Problem-solution fit feedback',
      'Priority access to pilot programs',
      'Founder-only closed sessions',
    ],
    cta: 'Apply for ASCENT',
    popular: true,
    testId: 'membership-tier-ascent',
  },
  {
    tier: 'Tier 3',
    name: 'Catalyst Circle',
    price: 'Custom / invite only',
    tagline: 'A private circle for CXOs, hospital leaders, pharma & investors.',
    features: [
      'Everything in THC Connect',
      'Closed CXO & investor forums',
      'Early access to vetted champions',
      'Co-creation & pilot opportunities',
      'End to End Innovation Program',
      'Strategic visibility across ecosystem',
    ],
    cta: 'Request Consideration',
    testId: 'membership-tier-catalyst',
  },
];

const COMPARISON_FEATURES = [
  { name: 'Newsletter & Updates', starter: true, ascent: true, catalyst: true },
  { name: 'Community Access (Slack/WhatsApp)', starter: true, ascent: true, catalyst: true },
  { name: 'Event Discounts', starter: '50%', ascent: '100%', catalyst: '100%' },
  { name: 'Weekly AMAs', starter: true, ascent: true, catalyst: true },
  { name: 'ASCENT Program', starter: false, ascent: true, catalyst: false },
  { name: 'Curated Intros & Matchmaking', starter: true, ascent: true, catalyst: true },
  { name: 'Closed CXO/Investor Forums', starter: false, ascent: true, catalyst: true },
  { name: 'Co-creation & Pilots', starter: false, ascent: true, catalyst: true },
];

const FAQS = [
  {
    q: 'What is the difference between THC Connect and ASCENT?',
    a: 'THC Connect gives you access to the THC community, events, and resources. ASCENT is a structured program for founders that includes clinical feedback, GTM support, and investor-ready preparation over a cohort period. ASCENT builds on THC Connect with intensive mentorship and demo day opportunities.',
  },
  {
    q: 'Can I upgrade my membership mid-year?',
    a: 'Yes! You can upgrade your membership at any time. We\'ll prorate the remaining period of your current plan toward the new tier. Contact our team for a seamless transition.',
  },
  {
    q: 'How does the Catalyst Circle application work?',
    a: 'Catalyst Circle is invite-only, designed for senior healthcare leaders, CXOs, and strategic investors. You can express interest through our application form, and our team will review and reach out if there\'s a fit.',
  },
  {
    q: 'What happens after I join — how does onboarding work?',
    a: 'After joining, you\'ll receive a welcome email with your login credentials, community access links, and a scheduled onboarding call. We\'ll help you get set up, connect with relevant members, and find the right events and programs for your goals.',
  },
];

const TESTIMONIALS = [
  { quote: 'THC helped me connect with oncologists and understand their diagnostics workflow better.', author: 'Healthtech Founder', role: 'CEO', company: 'Diagnostics Startup' },
  { quote: 'Through THC, we found our first hospital pilot within 6 weeks.', author: 'ASCENT Graduate', role: 'Founder', company: 'Digital Health' },
  { quote: 'This community gave us signal we couldn\'t get anywhere else.', author: 'Healthcare Investor', role: 'Partner', company: 'VC Fund' },
];

const CheckIcon = () => <Check className="w-4 h-4 text-[#1A7B6E] mx-auto" />;
const DashIcon = () => <Minus className="w-4 h-4 text-[#6B7280]/40 mx-auto" />;
const renderCell = (value) => {
  if (typeof value === 'string') return <span className="text-sm font-medium text-[#1A7B6E]">{value}</span>;
  return value ? <CheckIcon /> : <DashIcon />;
};

export default function Join() {
  return (
    <div data-testid="join-page">
      {/* Hero */}
      <section className="pt-12 sm:pt-16 lg:pt-20 pb-12 sm:pb-16" style={{ background: 'linear-gradient(180deg, rgba(13,27,62,0.04) 0%, rgba(245,240,232,1) 60%)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionTag className="text-center">THC Memberships</SectionTag>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-semibold text-[#0D1B3E] tracking-tight leading-[1.05] mb-6">
            Membership <span className="text-[#E8541A]">Pathways</span>
          </h1>
          <p className="text-xl sm:text-2xl text-[#6B7280] max-w-3xl mx-auto leading-relaxed">
            Find your place in India's largest healthtech community. Memberships are annual.
          </p>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-6 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TIERS.map((t) => (
              <PricingTierCard key={t.testId} {...t} />
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 sm:py-20 bg-white/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTag>Compare Plans</SectionTag>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#0D1B3E] tracking-tight mb-8">
            Compare All Tiers
          </h2>
          <div className="bg-white rounded-2xl border border-[rgba(13,27,62,0.10)] overflow-hidden shadow-[0_1px_0_rgba(13,27,62,0.06),0_14px_30px_rgba(13,27,62,0.08)]">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-[rgba(13,27,62,0.08)]">
                    <TableHead className="text-[#0D1B3E] font-semibold min-w-[200px]">Feature</TableHead>
                    <TableHead className="text-center text-[#0D1B3E] font-semibold">THC Connect</TableHead>
                    <TableHead className="text-center text-[#0D1B3E] font-semibold bg-[rgba(26,123,110,0.04)]">ASCENT</TableHead>
                    <TableHead className="text-center text-[#0D1B3E] font-semibold">Catalyst</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {COMPARISON_FEATURES.map((feat) => (
                    <TableRow key={feat.name} className="border-b border-[rgba(13,27,62,0.06)]">
                      <TableCell className="text-sm text-[#0D1B3E]/80">{feat.name}</TableCell>
                      <TableCell className="text-center">{renderCell(feat.starter)}</TableCell>
                      <TableCell className="text-center bg-[rgba(26,123,110,0.04)]">{renderCell(feat.ascent)}</TableCell>
                      <TableCell className="text-center">{renderCell(feat.catalyst)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTag>Hear from Members</SectionTag>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
            {TESTIMONIALS.map((t, i) => (
              <TestimonialCard key={i} {...t} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 bg-white/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionTag>FAQ</SectionTag>
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#0D1B3E] tracking-tight mb-8">
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="bg-white rounded-xl border border-[rgba(13,27,62,0.10)] px-5 shadow-sm"
              >
                <AccordionTrigger className="text-left text-sm font-semibold text-[#0D1B3E] hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-[#6B7280] leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}
